// src/app/shared/ui/ConnectionStatusBanner.tsx
import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Loader2, WifiOff, Wifi } from 'lucide-react';
import { OmegaWsService } from '@/sdk/OmegaWsService';
import { AppInitializationController, InitializationState } from '@/app/shared/app-init/AppInitializationController';
import { useTranslation } from '@/app/core/ui/theme/LanguageProvider';

type BannerVariant = 'reconnecting' | 'error' | 'restored' | null;
const RESTORED_DISPLAY_MS = 3000;

interface ConnectionStatusBannerProps {
  topInset?: number;
}

export const ConnectionStatusBanner = observer(function ConnectionStatusBanner({ topInset }: ConnectionStatusBannerProps) {
  const omegaService = OmegaWsService.getInstance();
  const client = omegaService.getOrCreateClient();
  const appInit = AppInitializationController.getInstance();
  const { t } = useTranslation();

  const [wsStatus, setWsStatus] = useState<string>(() => client.getStatus());
  const [showRestored, setShowRestored] = useState(false);
  const hasConnectedOnceRef = useRef(client.getStatus() === 'ready');
  const restoredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const offStatus = client.on('status', (status: string) => {
      setWsStatus(status);
      if (status === 'ready') {
        if (!hasConnectedOnceRef.current) {
          // Первый успешный коннект в этой сессии — баннер не показываем
          hasConnectedOnceRef.current = true;
        } else {
          // Повторное подключение — показываем "восстановлено"
          setShowRestored(true);
          if (restoredTimerRef.current) clearTimeout(restoredTimerRef.current);
          restoredTimerRef.current = setTimeout(() => setShowRestored(false), RESTORED_DISPLAY_MS);
        }
      } else {
        setShowRestored(false);
        if (restoredTimerRef.current) {
          clearTimeout(restoredTimerRef.current);
          restoredTimerRef.current = null;
        }
      }
    });

    return () => {
      offStatus();
      if (restoredTimerRef.current) clearTimeout(restoredTimerRef.current);
    };
  }, [client]);

  const appReady = appInit.state === InitializationState.READY;
  if (!appReady || !hasConnectedOnceRef.current) {
    return null;
  }

  let variant: BannerVariant = null;

  if (showRestored && wsStatus === 'ready') {
    variant = 'restored';
  } else if (wsStatus === 'reconnecting') {
    variant = 'reconnecting';
  } else if (wsStatus === 'closed' || wsStatus === 'error') {
    variant = 'error';
  }

  // Автоматическое восстановление при ошибке
  useEffect(() => {
    if (variant === 'error') {
      const timer = setTimeout(() => {
        appInit.reconnectWebSocketQuick().catch(() => {});
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [variant, appInit]);

  if (!variant) {
    return null;
  }

  return <Banner variant={variant} t={t as (key: string) => string} topInset={topInset} />;
});

function Banner({ variant, t, topInset }: { variant: NonNullable<BannerVariant>; t: (key: string) => string; topInset?: number }) {
  const styles = {
    reconnecting: {
      gradient: `linear-gradient(90deg, var(--warning-bg, rgba(234,179,8,0.18)), var(--warning-bg-light, rgba(245,158,11,0.08)))`,
      color: 'var(--warning-text, #a16207)',
      borderColor: 'var(--warning-border, rgba(245,158,11,0.18))',
      iconBg: 'var(--warning-icon-bg, rgba(245,158,11,0.16))',
      shadow: 'var(--warning-shadow, 0 10px 24px rgba(245,158,11,0.08))',
    },
    error: {
      gradient: `linear-gradient(90deg, var(--error-bg, rgba(239,68,68,0.18)), var(--error-bg-light, rgba(249,115,22,0.10)))`,
      color: 'var(--error-text, #b91c1c)',
      borderColor: 'var(--error-border, rgba(239,68,68,0.18))',
      iconBg: 'var(--error-icon-bg, rgba(239,68,68,0.16))',
      shadow: 'var(--error-shadow, 0 10px 24px rgba(239,68,68,0.08))',
    },
    restored: {
      gradient: `linear-gradient(90deg, var(--success-bg, rgba(34,197,94,0.16)), var(--success-bg-light, rgba(16,185,129,0.08)))`,
      color: 'var(--success-text, #15803d)',
      borderColor: 'var(--success-border, rgba(34,197,94,0.16))',
      iconBg: 'var(--success-icon-bg, rgba(34,197,94,0.16))',
      shadow: 'var(--success-shadow, 0 10px 24px rgba(34,197,94,0.08))',
    },
  }[variant];

  const bannerTexts = {
    reconnecting: t('wsReconnecting'),
    error: t('wsError'),
    restored: t('wsRestored'),
    retry: t('retry'),
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: `${topInset}px`,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '10px 16px',
        fontSize: '13px',
        fontWeight: 600,
        color: styles.color,
        background: styles.gradient,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${styles.borderColor}`,
        boxShadow: styles.shadow,
        animation: 'bannerSlideDown 0.25s ease-out',
      }}
    >
      <div
        style={{
          width: '28px',
          height: '28px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '999px',
          backgroundColor: styles.iconBg,
          flexShrink: 0,
        }}
      >
        {variant === 'reconnecting' && <Loader2 size={14} style={{ animation: 'bannerSpin 1s linear infinite' }} />}
        {variant === 'error' && <WifiOff size={14} />}
        {variant === 'restored' && <Wifi size={14} />}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {variant === 'reconnecting' && <span>{bannerTexts.reconnecting}</span>}
        {variant === 'error' && <span>{bannerTexts.error}</span>}
        {variant === 'restored' && <span>{bannerTexts.restored}</span>}
      </div>

      <style>{`
        @keyframes bannerSlideDown {
          from { opacity: 0; transform: translateY(-100%); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bannerSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}